import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { storage, db } from '../firebase/config';

/* ═══════════════════════════════════════════════════════════════
   🔒 OBTENIR UNE URL SIGNÉE SÉCURISÉE POUR UN LIVRE
   - Appelle la Cloud Function qui vérifie l'abonnement Gold
   - Retourne une URL signée valable 1 heure uniquement
   - L'URL expire automatiquement, empêchant le partage
   ═══════════════════════════════════════════════════════════════ */
export const getSecureBookUrl = async (fileName) => {
  try {
    const functions = getFunctions();
    const getSecureUrl = httpsCallable(functions, 'getSecureBookUrl');
    
    const result = await getSecureUrl({ fileName });
    
    if (result.data.success) {
      return { 
        success: true, 
        url: result.data.url,
        expiresIn: result.data.expiresIn
      };
    } else {
      return { success: false, error: 'Impossible de générer l\'URL sécurisée' };
    }
  } catch (error) {
    console.error('Erreur lors de la génération de l\'URL sécurisée:', error);
    
    // Messages d'erreur personnalisés
    let errorMessage = error.message;
    
    if (error.code === 'unauthenticated') {
      errorMessage = 'Vous devez être connecté pour accéder aux livres';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'Abonnement Gold requis pour accéder à la bibliothèque';
    } else if (error.code === 'not-found') {
      errorMessage = 'Livre introuvable';
    }
    
    return { success: false, error: errorMessage };
  }
};

/* ═══════════════════════════════════════════════════════════════
   UPLOAD D'UN LIVRE PDF
   - Upload vers Firebase Storage
   - Sauvegarde métadonnées dans Firestore
   ═══════════════════════════════════════════════════════════════ */
export const uploadBook = async (file, bookData, onProgress) => {
  try {
    // Créer une référence unique dans Storage
    const timestamp = Date.now();
    const fileName = `books/${timestamp}-${file.name}`;
    const storageRef = ref(storage, fileName);

    // Upload avec suivi de progression
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Progression de l'upload
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) onProgress(progress);
        },
        (error) => {
          console.error('Erreur upload:', error);
          reject({ success: false, error: error.message });
        },
        async () => {
          // Upload terminé avec succès
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // Sauvegarder les métadonnées dans Firestore
            const bookDoc = await addDoc(collection(db, 'books'), {
              ...bookData,
              pdfUrl: downloadURL,
              fileName: fileName, // ✅ Stocker le chemin dans Storage
              fileSize: file.size,
              uploadedAt: new Date()
            });

            resolve({ 
              success: true, 
              bookId: bookDoc.id,
              downloadURL,
              fileName: fileName // ✅ Retourner le fileName
            });
          } catch (error) {
            reject({ success: false, error: error.message });
          }
        }
      );
    });
  } catch (error) {
    console.error('Erreur lors de l\'upload:', error);
    return { success: false, error: error.message };
  }
};

/* ═══════════════════════════════════════════════════════════════
   RÉCUPÉRER TOUS LES LIVRES
   - Filtre optionnel par langue
   - Tri par date d'upload (plus récent en premier)
   ═══════════════════════════════════════════════════════════════ */
export const getAllBooks = async (language = null) => {
  try {
    // Récupérer tous les livres triés par date
    const q = query(collection(db, 'books'), orderBy('uploadedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    let books = [];
    querySnapshot.forEach((doc) => {
      books.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Filtrer par langue en JavaScript si nécessaire
    if (language) {
      books = books.filter(book => book.language === language);
    }

    return { success: true, books };
  } catch (error) {
    console.error('Erreur lors de la récupération des livres:', error);
    return { success: false, error: error.message };
  }
};

/* ═══════════════════════════════════════════════════════════════
   RÉCUPÉRER LES LIVRES PAR LANGUE
   ═══════════════════════════════════════════════════════════════ */
export const getBooksByLanguage = async (language) => {
  return getAllBooks(language);
};
